import { CircularProgress } from '@mui/material';
import { Box } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import { Add } from 'iconsax-react';
import { IStock } from 'types/custom/stock';
import { debounce } from 'lodash';
import React, { useMemo } from 'react';

type Props = {
  stocks: IStock[];
  selectedStock: IStock;
  setSelectedStock: (val: IStock) => void;
  handleAddToWatchList: (stockId: string, type: 'add' | 'delete') => void;
  stockSelectOpen: boolean;
  setStockSelectOpen: (val: boolean) => void;
};

const StockSelector: React.FC<Props> = React.memo(
  ({ stocks, selectedStock, stockSelectOpen, setSelectedStock, handleAddToWatchList, setStockSelectOpen }) => {
    const handleOnChangeAutoComplete = useMemo(
      () =>
        debounce((event: React.ChangeEvent<{}>, newValue: any) => {
          event.stopPropagation();
          const selectedStock = stocks.find((stock) => stock.stockId === newValue);
          if (selectedStock) {
            setSelectedStock(selectedStock);
            handleAddToWatchList(selectedStock.stockId, 'add');
          }
        }, 300),
      [stocks, setSelectedStock, handleAddToWatchList]
    );

    return (
      <Autocomplete
        id="basic-autocomplete-label"
        sx={{
          '& .MuiOutlinedInput-root': {
            p: 1
          },
          '& .MuiAutocomplete-tag': {
            bgcolor: 'primary.lighter',
            border: '1px solid',
            zIndex: 10000,
            borderColor: 'primary.light',
            '& .MuiSvgIcon-root': {
              color: 'primary.main',
              '&:hover': {
                color: 'primary.dark'
              }
            }
          }
        }}
        loading={stocks.length === 0}
        size="medium"
        disablePortal
        fullWidth
        open={stockSelectOpen}
        onOpen={() => setStockSelectOpen(true)}
        onClose={() => setStockSelectOpen(false)}
        options={stocks.map((item) => item.stockId)}
        value={selectedStock?.stockId || null}
        onChange={handleOnChangeAutoComplete}
        renderInput={(params) => (
          <TextField
            variant="outlined"
            {...params}
            placeholder="Search stocks"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {stocks.length === 0 && <CircularProgress color="inherit" size={20} />}
                </>
              )
            }}
          />
        )}
        noOptionsText="No stocks found"
        ListboxProps={{
          style: {
            height: '70vh'
          }
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              const iconButton = e.currentTarget.querySelector('button');
              if (iconButton) {
                iconButton.style.opacity = '1';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'inherit';
              const iconButton = e.currentTarget.querySelector('button');
              if (iconButton) {
                iconButton.style.opacity = '0';
              }
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <span>{option}</span>
              <IconButton
                size="small"
                color="success"
                variant="contained"
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.2s'
                }}
              >
                <Add fontSize="small" color="white" />
              </IconButton>
            </Box>
          </Box>
        )}
      />
    );
  }
);

export default StockSelector;
